import { useMemo, useState } from 'react';
import { Info } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { ScoreChip } from '../ds';
import GradeDetailModal from './GradeDetailModal';
import SubmissionViewer from '../overview/SubmissionViewer';
import { useSkillEvidence } from '../../data/hooks';
import { ordinalMedian } from '../../lib/discStats';

// A SKILL's level is the MEDIAN of its assessed specific skills — the same
// number the disc draws as that bar's cap, computed by the same function, so
// the sidebar and the disc cannot disagree. Rendered as the summary-variant
// chip (tint + cap): a roll-up never looks like a mark a teacher gave.
function skillMedian(specificSkills) {
  const scores = (specificSkills || [])
    .filter((s) => s.assessed)
    .map((s) => Number(s.score))
    .filter((n) => n >= 1 && n <= 5);
  return scores.length ? ordinalMedian(scores) : null;
}

// Each descriptor's `code` carries the standard's grade band at the front —
// "PK-1.CCE.1.7", "2-3.TIPS.2.1", "4-5.FL.1.2", "9-12.CCE.2.2" — never the
// 1–5 score, which is a different axis entirely (achievement, not curriculum
// level). A range uses its LOWER bound ("4-5" sorts as grade 4; "PK-1" sits
// before "K") so the order reads as one ascending Pre-K–12 line, assessed and
// unassessed rows interleaved. Same band → by code, then name. No code at all
// (not mapped to a standard) sorts last.
function gradeBandStart(code) {
  if (!code) return null;
  const prefix = code.split('.')[0];
  if (/^pk/i.test(prefix)) return -1;
  if (/^k/i.test(prefix)) return 0;
  const m = prefix.match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}

const byCode = (a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true });

function byGradeBand(specificSkills) {
  return [...(specificSkills || [])].sort((a, b) => {
    const ga = gradeBandStart(a.code);
    const gb = gradeBandStart(b.code);
    if (ga == null && gb == null) return a.name.localeCompare(b.name);
    if (ga == null) return 1;
    if (gb == null) return -1;
    return ga - gb || byCode(a, b) || a.name.localeCompare(b.name);
  });
}

function MedianChip({ specificSkills }) {
  const median = skillMedian(specificSkills);
  if (median == null) return null;
  return (
    <span className="ml-auto flex shrink-0 items-baseline gap-1.5">
      <span className="text-[10px] font-medium lowercase text-muted-foreground">median</span>
      <ScoreChip
        score={median}
        variant="summary"
        title="The skill's level: median of its assessed skill developmental descriptors below — ties round down. Same number as the disc bar's cap."
      />
    </span>
  );
}

// An assessed skill opens its score anatomy (media-ux §5): the tally of
// contributing assessments behind the median, each with its evidence and
// the rubric it was judged under. Unassessed rows stay inert — there is
// nothing behind them yet.
function SkillRow({ s, onAnatomy }) {
  const body = (
    <>
      <span
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          s.assessed ? 'bg-success' : 'bg-muted-foreground/35'
        )}
      />
      <span className="min-w-0 flex-1 text-left text-[12.5px] leading-snug text-foreground">
        {s.code && (
          <span className="mr-1.5 font-mono text-[10.5px] font-bold text-primary">{s.code}</span>
        )}
        {s.name}
      </span>
      {s.assessed ? (
        <ScoreChip score={s.score} />
      ) : (
        <span className="shrink-0 rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          Not assessed
        </span>
      )}
    </>
  );
  if (!s.assessed) return <div className="flex items-start gap-2 py-1">{body}</div>;
  return (
    <button
      type="button"
      onClick={() => onAnatomy(s)}
      title="What made this score?"
      className="-mx-1.5 flex items-start gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-secondary/60"
    >
      {body}
    </button>
  );
}

export default function SkillsSidebar({ clickedNode, studentId, studentName }) {
  // Score anatomy: which specific skill is open, if any.
  const [anatomySkill, setAnatomySkill] = useState(null);
  // "Open the work": which project's container to show in the viewer —
  // anatomy rows span projects, so each entry opens its own.
  const [workCtx, setWorkCtx] = useState(null);
  const evidence = useSkillEvidence(anatomySkill ? studentId : null, anatomySkill?.id);

  const data = useMemo(() => {
    if (!clickedNode) return null;

    if (clickedNode.depth === 3) {
      const ss = clickedNode.specificSkills;
      let specificSkills;
      if (Array.isArray(ss) && ss.length) {
        specificSkills = ss;
      } else {
        const kids = clickedNode.children || [];
        specificSkills = kids.map((k) => ({
          id: k.id,
          name: k.name,
          code: k.code ?? null,
          assessed: !!k.assessed,
          score: k.averageScore ?? k.score ?? 0,
        }));
      }
      const assessedCount = specificSkills.filter((s) => s.assessed).length;
      const parentName = clickedNode.parent?.name || '';
      return { type: 'skill', name: clickedNode.name, parentName, specificSkills, assessedCount };
    }

    if (clickedNode.depth === 2) {
      const areaName = clickedNode.parent?.name || '';
      const compName = clickedNode.name || '';
      const skills = (clickedNode.children || []).map((skill) => {
        const ss = skill.specificSkills || skill.children || [];
        const specific = ss.map((k) => ({
          id: k.id,
          name: k.name,
          code: k.code ?? null,
          assessed: !!k.assessed,
          score: k.averageScore ?? k.score ?? 0,
        }));
        return {
          id: skill.id,
          name: skill.name,
          assessed: !!skill.assessed,
          _assessedCount: skill._assessedCount || specific.filter((s) => s.assessed).length,
          _totalCount: skill._totalCount || specific.length,
          specificSkills: specific,
        };
      });
      const totalAssessed = skills.reduce((sum, s) => sum + s._assessedCount, 0);
      const totalCount = skills.reduce((sum, s) => sum + s._totalCount, 0);
      return { type: 'competency', areaName, compName, skills, totalAssessed, totalCount };
    }

    return null;
  }, [clickedNode]);

  return (
    <div className="ds h-fit overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-bold text-foreground">Skill Developmental Descriptors</h3>
        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground">
          {data?.type === 'skill' ? (
            <>
              <strong className="font-semibold text-foreground">{data.name}</strong>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                {data.assessedCount}/{data.specificSkills.length} assessed
              </span>
              <MedianChip specificSkills={data.specificSkills} />
            </>
          ) : data?.type === 'competency' ? (
            <>
              <strong className="font-semibold text-foreground">{data.compName}</strong>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                {data.totalAssessed}/{data.totalCount} assessed
              </span>
            </>
          ) : (
            'Click a skill or competency to view skill developmental descriptors'
          )}
        </p>
      </div>

      <div className="max-h-[640px] overflow-y-auto p-4">
        {!data ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-[13px] text-muted-foreground">
            <Info className="size-10 opacity-40" />
            <p>Click a skill or competency on the disc to view skill developmental descriptors</p>
          </div>
        ) : data.type === 'competency' ? (
          <>
            <div className="mb-3 border-b border-border pb-2.5 text-[13px] text-muted-foreground">
              <strong className="font-semibold text-foreground">{data.areaName}</strong>
              <span className="opacity-50"> › </span>
              {data.compName}
            </div>
            {data.skills.map((skill) => (
              <div key={skill.id} className="mb-4">
                <div className="mb-1.5 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className="min-w-0">
                    {skill.name} ({skill._assessedCount}/{skill._totalCount})
                  </span>
                  <MedianChip specificSkills={skill.specificSkills} />
                </div>
                <div className="flex flex-col">
                  {byGradeBand(skill.specificSkills).map((s) => (
                    <SkillRow key={s.id} s={s} onAnatomy={setAnatomySkill} />
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : data.specificSkills.length === 0 ? (
          <div className="py-8 text-center text-[13px] text-muted-foreground">
            No skill developmental descriptors found for this skill.
          </div>
        ) : (
          <>
            {data.parentName && (
              <div className="mb-3 border-b border-border pb-2.5 text-[13px] text-muted-foreground">
                {data.parentName}
              </div>
            )}
            <div className="flex flex-col">
              {byGradeBand(data.specificSkills).map((s) => (
                <SkillRow key={s.id} s={s} onAnatomy={setAnatomySkill} />
              ))}
            </div>
          </>
        )}
      </div>

      <GradeDetailModal
        open={Boolean(anatomySkill)}
        onClose={() => setAnatomySkill(null)}
        studentName={studentName || 'Student'}
        skillName={anatomySkill?.name || ''}
        breadcrumb={null}
        grades={evidence.data?.items ?? []}
        loading={evidence.isLoading}
        anatomy
        onOpenWork={(g, anchor) =>
          setWorkCtx({
            projectId: g.project_id,
            student: { student_id: Number(studentId), student_name: studentName },
            anchor: anchor ?? null,
          })
        }
      />

      <SubmissionViewer
        projectId={workCtx?.projectId}
        student={workCtx ? workCtx.student : null}
        anchor={workCtx?.anchor ?? null}
        onClose={() => setWorkCtx(null)}
      />
    </div>
  );
}
